import Container, { Service } from 'typedi';
import { LoggerProvider } from '../logger';
import { IPostMessageHandler, IPostMessageService } from '../post-message';
import {
    ActionID,
    AppIdentifiers,
    AreaType,
    TriggerType,
    IPerformActionTopicPayload,
    ILogger,
} from '@sparkware/uc-sdk-core';
import { IActionHandlerFactory } from '../action-handler/models/IActionHandlerFactory';
import { Utils } from '../utils';
import { LoaderManager } from '../../loaders/LoaderManager';
import { WindowToken } from '../../injection-tokens';
import { RegistrationOldDataService } from './registration.old.data.service';
import { ICloseButtonVisibility } from './models';
import { NativeUtils } from '../native/native.utils';
import { ViewInjector } from '../view-injector';
import { GNWPUtils } from '../gnwp/gnwp-utils';
import { FYMode } from './enums';

@Service()
export class Registration {
    private readonly _window: Window;
    private readonly _logger: ILogger;
    private readonly _utils: Utils;
    private readonly _registrationOldDataService: RegistrationOldDataService;
    private readonly _viewInjector: ViewInjector;
    private readonly _gnwpUtils: GNWPUtils;

    private get _actionHandlerFactoryPromise(): Promise<IActionHandlerFactory> {
        return LoaderManager.Instance.ActionHandlerFactoryLoader.Instance;
    }

    private get _registrationHandlerPromise(): Promise<IPostMessageHandler> {
        return LoaderManager.Instance.RegistrationHandlerLoader.Instance;
    }

    private get _postMessageServicePromise(): Promise<IPostMessageService> {
        return LoaderManager.Instance.PostMessageServiceLoader.Instance;
    }

    constructor() {
        this._window = Container.get(WindowToken);
        this._logger = Container.get(LoggerProvider).getLogger('Registration');
        this._utils = Container.get(Utils);
        this._registrationOldDataService = Container.get(RegistrationOldDataService);
        this._viewInjector = Container.get(ViewInjector);
        this._gnwpUtils = Container.get(GNWPUtils);

        void this._registerPostMessageHandler();
    }

    public _registerPostMessageHandler = async (): Promise<void> => {
        const handler = await this._registrationHandlerPromise;
        this._logger.debug(`Registering ${handler.name}...`);
        const postMessageService = await this._postMessageServicePromise;
        postMessageService?.addHandler(handler);
    };

    public openRegistration = async (): Promise<void> => {
        const { clientVersion } = this._window.pageContextManager.getDeviceData();
        const openRegister: IPerformActionTopicPayload = {
            actionID: ActionID.register,
            correlationID: this._utils.getCorrelationId(),
            launchInfo: {
                businessCorrelationID: this._utils.generateCorrelationID(),
                channel: {
                    area: AreaType.button,
                    source: 'Unified Client',
                    element: 'Default',
                },
                sourceAppID: AppIdentifiers.UnifiedClient,
                sourceAppVersion: '0.0.0',
                appSpecificParameters: {},
                trigger: TriggerType.userSelection,
                clientVersion: clientVersion,
            },
            actionData: {},
        };
        const actionHandlerFactory = await this._actionHandlerFactoryPromise;
        await actionHandlerFactory.performAction(openRegister);
    };

    public openOldRegistration = async (
        width: number,
        height: number,
        dlParam?: any,
        lang?: string,
        onCloseCallback?: () => void,
    ): Promise<void> => {
        const registrationData = await this._registrationOldDataService.getRegistrationData();
        if (!registrationData) {
            this._logger.error('Failed to retrieve registration data');
            return;
        }

        const membershipParams = this._registrationOldDataService.buildMemberShipParams(
            registrationData,
            dlParam,
            null,
            false,
        );

        if (lang !== undefined) {
            if ('LangID' in membershipParams) {
                const langIdParamValue = lang.toLowerCase();
                membershipParams['LangID'] = langIdParamValue;

                if (langIdParamValue === 'ar') {
                    membershipParams['arfrm'] = 1;
                }
            }
        }

        var hasCloseButton = this._registrationOldDataService.hasCloseButton();

        await this.openRegistrationIframe(width, height, hasCloseButton, onCloseCallback);

        this._gnwpUtils.addFormToBody(
            registrationData.GnwpUrl,
            membershipParams,
            'post',
            'generalIframe',
        );
    };

    public openFYP = async (width: number, height: number): Promise<void> => {
        await this.openFY(FYMode.FYP, width, height);
    };
    public openFYU = async (width: number, height: number): Promise<void> => {
        await this.openFY(FYMode.FYU, width, height);
    };

    private openRegistrationIframe = async (
        width: number,
        height: number,
        hasCloseButton: ICloseButtonVisibility,
        onCloseCallback?: () => void,
    ): Promise<void> => {
        await NativeUtils.showBottomBar(false);

        const onClose = async () => {
            await NativeUtils.showBottomBar(true);
            if (onCloseCallback) {
                onCloseCallback();
            }
        };

        const { isMobile } = this._window.pageContextManager.getDeviceData();
        if (isMobile) {
            this._viewInjector.OpenMobileContainerDiv('', hasCloseButton.mobile, onClose);
        } else {
            if (hasCloseButton.pc) {
                this._viewInjector.InjectWrappingDivWithCloseButton(
                    '',
                    width,
                    height,
                    true,
                    onClose,
                );
            } else {
                this._viewInjector.InjectWrappingDiv('', width, height, true);
            }
        }
    };

    private openFY = async (mode: FYMode, width, height): Promise<void> => {
        const registrationData = await this._registrationOldDataService.getRegistrationData();
        if (!registrationData) {
            this._logger.error('Failed to retrieve registration data');
            return;
        }

        const membershipParams = this._registrationOldDataService.buildMemberShipParams(
            registrationData,
            null,
            mode,
            false,
        );

        const hasCloseButton: ICloseButtonVisibility = {
            mobile: false,
            pc: true,
        };

        await this.openRegistrationIframe(width, height, hasCloseButton, undefined);
        this._gnwpUtils.addFormToBody(
            registrationData.GnwpUrl,
            membershipParams,
            'post',
            'generalIframe',
        );
    };
}
